import * as React from 'react'
import moment from 'moment'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import {
  API,
  InventoryFilter,
  IInventoryList,
  IUpdateAuditRequest,
} from '@getgreenline/homi-shared'
import { DateRangePicker } from 'react-dates'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { Routes } from '../../../../../../constants/Routes'
import { message, Switch, Tooltip, Icon, Spin } from 'antd'
import { parseErrorMsg, getCategoryName } from '../../../../../../utilities/helpers'
import './auditBulkImportModal.scss'
import { ISelectOption, ReactMultiSelect } from '../../../../../../components/ReactSelect'
import { AuditStore } from '../EditAuditStore'
import { CurrentUserStore } from '../../../../../../stores/CurrentUserStore'
import { AuditModels, AuditProductModels, AuditProductAPI } from '@getgreenline/audits'
import { BlazeButton } from '../../../../../../components/CustomButtons/BlazeButton'

interface Props {
  audit: AuditModels.CreateInventoryAudit
  auditStore: AuditStore
  onHide: () => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

type TFocusedDateInput = 'startDate' | 'endDate' | null

interface State {
  bulkInventoryList: IInventoryList[]
  isInventoryListOpen: boolean
  isLoadingInventoryList: boolean
  isImporting: boolean
  focusedInput: TFocusedDateInput
  showDateRestriction: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class AuditBulkImportModal extends React.Component<Props & InjectedProps, State> {
  inventoryFilter = new InventoryFilter({
    categoryIds: [],
    supplierIds: [],
    startDate: undefined,
    endDate: undefined,
    isInStock: false,
    onlyNewProducts: false,
  })

  constructor(props: Props) {
    super(props)

    this.state = {
      bulkInventoryList: [],
      isInventoryListOpen: false,
      isLoadingInventoryList: false,
      isImporting: false,
      focusedInput: null,
      showDateRestriction: false,
    }
  }

  componentDidMount = async () => {
    this.requestInventoryList()
  }

  toggleInventoryList = () =>
    this.setState({ isInventoryListOpen: !this.state.isInventoryListOpen })

  addDateToFilter = async (date: {
    startDate: moment.Moment | null
    endDate: moment.Moment | null
  }) => {
    const newStartDate = date.startDate ? date.startDate.startOf('day').toDate() : undefined
    const newEndDate = date.endDate ? date.endDate.endOf('day').toDate() : undefined

    this.inventoryFilter.setStartDate(newStartDate)
    this.inventoryFilter.setEndDate(newEndDate)

    await this.requestInventoryList()
  }

  addCategoryIdsToFilter = async (selectedOptions: ISelectOption[] | null) => {
    const categoryIds =
      selectedOptions === null
        ? undefined
        : selectedOptions.map((selectedOption) => selectedOption.value)

    this.inventoryFilter.setCategoryIds(categoryIds)
    await this.requestInventoryList()
  }

  addSupplierIdsToFilter = async (selectedOptions: ISelectOption[] | null) => {
    const supplierIds =
      selectedOptions === null
        ? undefined
        : selectedOptions.map((selectedOption) => parseInt(selectedOption.value))

    this.inventoryFilter.setSupplierIds(supplierIds)
    await this.requestInventoryList()
  }

  setFocusedInput = (focusedInput: TFocusedDateInput) => {
    this.setState({ focusedInput })
  }

  toggleIsInStock = async () => {
    this.inventoryFilter.setIsInStock(!this.inventoryFilter.isInStock)

    await this.requestInventoryList()
  }

  requestInventoryList = async () => {
    await this.setState({ isLoadingInventoryList: true })

    const { audit } = this.props

    try {
      const bulkInventoryList = await API.getInventoryList(
        audit.companyId,
        [audit.locationId],
        this.inventoryFilter,
      )
      this.setState({ bulkInventoryList })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error exporting inventory list')
    }
    this.setState({ isLoadingInventoryList: false })
  }

  submitImportForm = async () => {
    const { audit } = this.props

    try {
      this.setState({ isImporting: true })

      const auditProductFilter = new AuditProductModels.AuditProductFilter({
        ...this.inventoryFilter,
        onlyNewProducts: !!this.inventoryFilter.onlyNewProducts,
      })

      await AuditProductAPI.bulkImportAuditProducts(audit.companyId, audit.id, auditProductFilter)

      const auditResponse = await API.getInventoryAuditById(audit.companyId, audit.id)

      audit.populate(auditResponse)

      this.setState({ isImporting: false })

      this.props.onHide()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error while adding audit products')
    } finally {
      this.setState({ isImporting: false })
    }
  }

  toggleDateRestriction = async () => {
    if (this.state.showDateRestriction) {
      this.setState({ showDateRestriction: false })
      this.inventoryFilter.setStartDate()
      this.inventoryFilter.setEndDate()
      await this.requestInventoryList()
    } else {
      this.setState({ showDateRestriction: true })
    }
  }

  render() {
    const { flattenedCategories } = this.props.currentCompanyStore!.productStore!
    const { suppliersWithName } = this.props.currentCompanyStore!.supplierStore!

    const { audit, onHide } = this.props
    const {
      isLoadingInventoryList,
      isInventoryListOpen,
      isImporting,
      focusedInput,
      bulkInventoryList,
    } = this.state

    const startDate = this.inventoryFilter.startDate ? moment(this.inventoryFilter.startDate) : null
    const endDate = this.inventoryFilter.endDate ? moment(this.inventoryFilter.endDate) : null

    return (
      <Modal isOpen={true} keyboard={false}>
        <Spin spinning={this.state.isImporting} wrapperClassName='audit-bulk-import-loader'>
          <ModalHeader toggle={onHide}>Bulk product import</ModalHeader>

          <ModalBody>
            <div className='form-group'>
              <label className='mb-2' htmlFor='bulk-import-categories'>
                Categories
              </label>

              <ReactMultiSelect
                id='bulk-import-categories'
                isDisabled={this.inventoryFilter.onlyNewProducts}
                options={flattenedCategories.map((category) => ({
                  label: getCategoryName(category),
                  value: category.id,
                }))}
                onChange={this.addCategoryIdsToFilter}
              />
            </div>

            <div className='form-group'>
              <label htmlFor='bulk-import-vendors' className='mb-2'>
                Brands
              </label>

              <ReactMultiSelect
                id='bulk-import-vendors'
                isClearable
                isDisabled={this.inventoryFilter.onlyNewProducts}
                options={suppliersWithName.map((supplier) => ({
                  label: supplier.name,
                  value: supplier.id,
                }))}
                onChange={this.addSupplierIdsToFilter}
              />
            </div>

            {this.state.showDateRestriction && (
              <div className='form-group'>
                <label htmlFor='bulk-import-vendors' className='mb-2'>
                  Products active within
                </label>

                <div className='bulk-import-date-range'>
                  <DateRangePicker
                    showClearDates
                    startDate={startDate}
                    startDateId='bulk-import-start-date'
                    endDate={endDate}
                    endDateId='bulk-import-end-date'
                    onDatesChange={this.addDateToFilter}
                    focusedInput={focusedInput}
                    onFocusChange={this.setFocusedInput}
                    isOutsideRange={() => false}
                  />
                </div>
              </div>
            )}

            <hr />

            <div className='mt-3'>
              <p className='mb-2'>Options</p>

              {this.props.currentUserStore?.currentUser?.isAdmin && (
                <div className='d-flex align-items-center mb-2'>
                  <span style={{ fontSize: 12 }}>Only import new products</span>
                  <Switch
                    className='ml-auto'
                    checked={this.inventoryFilter.onlyNewProducts}
                    onChange={async () => {
                      this.inventoryFilter.setOnlyNewProducts(!this.inventoryFilter.onlyNewProducts)

                      if (this.inventoryFilter.onlyNewProducts) {
                        this.inventoryFilter.setCategoryIds()
                        this.inventoryFilter.setSupplierIds()
                        this.inventoryFilter.setStartDate()
                        this.inventoryFilter.setEndDate()
                      }

                      this.requestInventoryList()
                    }}
                  />
                </div>
              )}

              <div className='d-flex align-items-center mb-2'>
                <span style={{ fontSize: 12 }}>Only show in-stock products</span>
                <Switch
                  className='ml-auto'
                  checked={this.inventoryFilter.isInStock}
                  disabled={this.inventoryFilter.onlyNewProducts}
                  onChange={this.toggleIsInStock}
                />
              </div>

              <div className='d-flex align-items-center'>
                <span style={{ fontSize: 12 }}>
                  Active inventory date restriction{' '}
                  <Tooltip title='This will only add products that were moved within the time range given. If no filter, we will import all products'>
                    <Icon type='info-circle' />
                  </Tooltip>
                </span>
                <Switch
                  className='ml-auto'
                  checked={this.state.showDateRestriction}
                  disabled={this.inventoryFilter.onlyNewProducts}
                  onChange={this.toggleDateRestriction}
                />
              </div>
            </div>

            <div
              className='btn-group w-100 mt-3'
              role='group'
              aria-label='Button group with nested dropdown'
            >
              <BlazeButton
                buttonProps={{
                  onClick: this.submitImportForm,
                  type: 'primary',
                  className: 'btn btn-success w-100',
                  disabled: isLoadingInventoryList || isImporting,
                  size: 'large',
                }}
                titleProps={{
                  title: (
                    <span>
                      {`${isImporting ? 'Importing' : 'Import'} ${
                        bulkInventoryList.length
                      } products`}
                    </span>
                  ),
                }}
              />

              <button
                type='button'
                className='btn dropdown-border dropdown-toggle'
                data-toggle='dropdown'
                aria-haspopup='true'
                aria-expanded='false'
                onClick={this.toggleInventoryList}
              ></button>
            </div>
          </ModalBody>

          {bulkInventoryList.length > 0 && isInventoryListOpen && (
            <ModalFooter>
              <ul>
                {bulkInventoryList.map((listItem, i) => {
                  return (
                    <li key={`list-item-${i}`}>
                      <a
                        href={Routes.EDIT_PRODUCT(
                          audit.companyId,
                          listItem.parentProductId || listItem.productId,
                        )}
                        target='_blank'
                        rel='noreferrer'
                      >
                        {listItem.name}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </ModalFooter>
          )}
        </Spin>
      </Modal>
    )
  }
}
